/**
 * LienSummaryCard
 * Tarjeta reutilizable que muestra el estado de un WalletLien (cobro latente) y
 * el historial de cobros sobre el. Usada como embed en CreditDetailPage y como
 * cuerpo principal de LienDetailPage.
 */

import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import {
  formatCurrencyMXN,
  formatPercentage,
} from '@/features/collection/types';
import type { WalletLien, LienCollectionItem } from '@/types/lien.types';

interface LienSummaryCardProps {
  lien: WalletLien;
  collections: LienCollectionItem[];
  showCreditLink?: boolean;
}

function formatDate(value: string | number | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'number' ? new Date(value) : new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-MX', {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function statusBadgeVariant(status: string): 'default' | 'success' | 'warning' | 'danger' {
  switch (status) {
    case 'ACTIVE':   return 'warning';
    case 'PAID_OFF': return 'success';
    case 'EXPIRED':  return 'danger';
    case 'WAIVED':   return 'default';
    default:         return 'default';
  }
}

function collectionStatusBadge(status: string): 'default' | 'success' | 'warning' | 'danger' {
  switch (status) {
    case 'FULL':    return 'success';
    case 'PARTIAL': return 'warning';
    case 'NONE':    return 'danger';
    default:        return 'default';
  }
}

export function LienSummaryCard({ lien, collections }: LienSummaryCardProps) {
  const recoveryPct = lien.original_balance_mxn > 0
    ? (lien.total_collected_mxn / lien.original_balance_mxn) * 100
    : 0;

  return (
    <Card title="Cobro Latente" description="Retencion sobre el wallet del cliente">
      <div className="space-y-4">
        {/* Status + dates */}
        <div className="flex flex-wrap items-center gap-4">
          <Badge variant={statusBadgeVariant(lien.status)}>{lien.status}</Badge>
          <span className="text-sm text-muted-foreground">
            Prescripcion: {lien.expiration_date ?? '—'}
          </span>
          <span className="text-sm text-muted-foreground">
            Intentos: {lien.collection_attempts ?? 0}
          </span>
          {lien.last_collection_date && (
            <span className="text-sm text-muted-foreground">
              Ultimo cobro: {formatDate(lien.last_collection_date)}
            </span>
          )}
          {lien.paid_off_date && (
            <span className="text-sm text-success">
              Liquidado: {formatDate(lien.paid_off_date)}
            </span>
          )}
        </div>

        {/* Balance + recovery progress */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <BalanceTile label="Balance original" value={formatCurrencyMXN(lien.original_balance_mxn)} />
          <BalanceTile label="Recuperado" value={formatCurrencyMXN(lien.total_collected_mxn)} />
          <BalanceTile label="Balance restante" value={formatCurrencyMXN(lien.current_balance_mxn)} />
        </div>
        <ProgressBar pct={recoveryPct} />

        {/* Split snapshot */}
        <div className="text-sm">
          <span className="text-muted-foreground">Split snapshot: </span>
          <span className="font-medium">Partner {formatPercentage(lien.partner_share_pct * 100)}</span>
          <span className="text-muted-foreground"> / </span>
          <span className="font-medium">Fiado {formatPercentage(lien.fiado_share_pct * 100)}</span>
          <span className="ml-2 text-muted-foreground">(partner = {lien.partner_id})</span>
        </div>

        {/* Collection history table */}
        <div>
          <h3 className="mb-2 text-sm font-semibold">Historial de cobros</h3>
          {collections.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aun no hay intentos de cobro.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-muted-foreground">
                  <tr>
                    <th className="p-2">Fecha</th>
                    <th className="p-2 text-right">Solicitado</th>
                    <th className="p-2 text-right">Cobrado</th>
                    <th className="p-2 text-right">Partner</th>
                    <th className="p-2 text-right">Fiado</th>
                    <th className="p-2">Status</th>
                    <th className="p-2">Transfer</th>
                  </tr>
                </thead>
                <tbody>
                  {collections.map(c => (
                    <tr key={c.record_id} className="border-b last:border-0">
                      <td className="p-2">{formatDate(c.collection_date)}</td>
                      <td className="p-2 text-right">{formatCurrencyMXN(c.amount_requested_mxn)}</td>
                      <td className="p-2 text-right font-medium">{formatCurrencyMXN(c.amount_collected_mxn)}</td>
                      <td className="p-2 text-right">{formatCurrencyMXN(c.partner_portion_mxn)}</td>
                      <td className="p-2 text-right">{formatCurrencyMXN(c.fiado_portion_mxn)}</td>
                      <td className="p-2">
                        <Badge variant={collectionStatusBadge(c.collection_status)}>{c.collection_status}</Badge>
                      </td>
                      <td className="p-2 text-xs text-muted-foreground">{c.transfer_status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function BalanceTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-border bg-muted/30 p-3">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  );
}

function ProgressBar({ pct }: { pct: number }) {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>Recuperacion</span>
        <span>{clamped.toFixed(1)}%</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded bg-muted">
        <div
          className="h-full bg-success transition-all"
          style={{ width: `${clamped}%` }}
        />
      </div>
    </div>
  );
}
